// src/layout/Sidebar.jsx
import React from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Tooltip,
  IconButton,
  Typography,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
} from "@mui/material";
import {
  Store,
  VideoLibrary,
  Article,
  Chat,
  ShoppingBag,
  Pets,
  History,
  MenuRounded,
  HelpOutlineRounded,
  People,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import colors from "../constants/colors";
import { Image as ImageIcon } from "@mui/icons-material";

const menuItems = [
  { text: "Market", icon: <Store />, path: "/market", public: true },
  { text: "Videos", icon: <VideoLibrary />, path: "/videos", public: false },
  { text: "Articles", icon: <Article />, path: "/articles", public: false },
  { text: "Charcha", icon: <Chat />, path: "/charcha", public: false },
  { text: "Products", icon: <ShoppingBag />, path: "/products", public: false },
  {
    text: "Cowshed Animals",
    icon: <Pets />,
    path: "/cowshed-animals",
    public: false,
  },
  
  { text: "All Users", icon: <People />, path: "/all-users", public: false },
  { text: "Banners", icon: <ImageIcon />, path: "/banners", public: false }, // new
  { text: "Own Banners", icon: <ImageIcon />, path: "/own-banners", public: false },
  {
    text: "Order History",
    icon: <History />,
    path: "/order-history",
    public: false,
  },
  {
    text: "Delivery Charges",
    icon: <History />,
    path: "/delivery-charges",
    public: false,
  }, // new
  { text: "FAQ's", icon: <HelpOutlineRounded />, path: "/faqs", public: true },
  {
    text: "Help & Support",
    icon: <HelpOutlineRounded />,
    path: "/support",
    public: true,
  },
];

const Sidebar = ({ open, onToggle, user, onLoginRequired }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:800px)");
  const drawerWidth = open ? 250 : 60;
  const [loginDialogOpen, setLoginDialogOpen] = React.useState(false);
  const [clickedItem, setClickedItem] = React.useState(null);

  const handleMenuItemClick = (item) => {
    if (!user && !item.public) {
      // Show login dialog for protected routes and store the clicked item
      setClickedItem(item);
      setLoginDialogOpen(true);
      return;
    }

    // Navigate to the route if user is logged in or it's a public route
    navigate(item.path);
    if (isMobile) onToggle();
  };

  const handleLoginClick = () => {
    setLoginDialogOpen(false);
    // Navigate to root path which will show the login screen
    onLoginRequired();
  };

  const handleCloseDialog = () => {
    setLoginDialogOpen(false);
    setClickedItem(null);
  };

  return (
    <>
      <Drawer
        key={isMobile ? "mobile" : "desktop"}
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? open : true}
        onClose={onToggle}
        ModalProps={{
          keepMounted: true,
          BackdropProps: {
            invisible: !isMobile,
          },
        }}
        sx={{
          width: isMobile ? 0 : drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            background: `linear-gradient(180deg, ${colors.white} 0%, ${colors.lightMint} 100%)`,
            borderRight: `1px solid ${colors.lightGray}`,
            transition: "width 0.3s ease",
            overflowX: "hidden",
          },
        }}
      >
        <Box
          className="sidebar"
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            height: "100%",
            overflowY: "hidden",
          }}
        >
          {/* Header */}
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: open ? "space-between" : "center",
              alignItems: "center",
              px: open ? 2 : 0,
            }}
          >
            {open && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: colors.green,
                  fontSize: "1.2rem",
                  letterSpacing: 0.5,
                }}
              >
                OK Market
              </Typography>
            )}
            <IconButton
              onClick={onToggle}
              sx={{
                color: colors.green,
                "&:hover": { backgroundColor: colors.lightMint },
              }}
            >
              <MenuRounded />
            </IconButton>
          </Toolbar>

          {/* Menu - Show ALL items with normal appearance */}
                {/* Menu Container with Scrollbar */}
          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            {/* Menu - Show ALL items with normal appearance */}
            <List sx={{ mt: 1.5 }}>
              {menuItems.map((item) => {
                const isSelected = location.pathname === item.path;

                return (
                  <Tooltip
                    title={!open ? item.text : ""}
                    placement="right"
                    key={item.text}
                    arrow
                  >
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => handleMenuItemClick(item)}
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? "flex-start" : "center",
                          px: open ? 2 : 1.2,
                          mx: open ? 1 : 0.5,
                          my: 0.5,
                          borderRadius: "12px",
                          transition: "background-color 0.3s ease",
                          backgroundColor: isSelected
                            ? colors.green
                            : "transparent",
                          "&.Mui-selected": {
                            backgroundColor: `${colors.green} !important`,
                          },
                          "&:hover": {
                            backgroundColor: isSelected
                              ? colors.green
                              : colors.lightMint,
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            color: isSelected ? colors.white : colors.darkGray,
                            transition: "color 0.3s ease",
                            justifyContent: "center",
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>

                        <ListItemText
                          primary={item.text}
                          sx={{
                            opacity: open ? 1 : 0,
                            transition: "opacity 0.3s ease",
                            "& .MuiTypography-root": {
                              color: isSelected ? colors.white : colors.darkBlack,
                              fontWeight: isSelected ? 600 : 400,
                              whiteSpace: "nowrap",
                            },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                );
              })}
            </List>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: open ? 2 : 0,
              textAlign: open ? "left" : "center",
              mb: 1.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: colors.darkGray,
                fontSize: "0.8rem",
                opacity: 0.8,
              }}
            >
              © 2025 OK Market
            </Typography>
          </Box>
        </Box>
      </Drawer>

      {/* Login Required Dialog */}
      <Dialog
        open={loginDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: colors.darkGreen,
            fontSize: "1.3rem",
            pb: 1,
          }}
        >
          Login Required
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{
              textAlign: "center",
              color: colors.darkGray,
              fontSize: "1rem",
              lineHeight: 1.6,
              mb: 2,
            }}
          >
            To access{" "}
            <strong style={{ color: colors.darkGreen }}>
              {clickedItem?.text}
            </strong>
            , you need to login to your OK Market Admin account.
          </DialogContentText>

          <Box
            sx={{
              textAlign: "center",
              mt: 3,
              p: 2,
              backgroundColor: colors.lightMint,
              borderRadius: 2,
              border: `1px solid ${colors.green}20`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: colors.darkGray,
                fontWeight: 500,
              }}
            >
              🔒 This feature is available only for authorized administrators
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 2,
            pb: 3,
            pt: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCloseDialog}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              borderColor: colors.darkGray,
              color: colors.darkGray,
              "&:hover": {
                backgroundColor: "#f5f5f5",
                borderColor: colors.darkGray,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLoginClick}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1,
              fontWeight: 600,
              backgroundColor: colors.green,
              "&:hover": {
                backgroundColor: colors.darkGreen,
              },
            }}
          >
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Sidebar;
